import { useFileViewer, useSection, useServices, useToast } from '@/app/providers';
import { SectionSelector } from '@/modules/sections/ui/components/SectionSelector';
import { useDateFormatter } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import { Announcement, AnnouncementType } from '@/shared/types';
import { AlertTriangle, BarChart2, Bell, Calendar, CheckCircle, Filter, Info, Loader2, Megaphone, Paperclip, PartyPopper } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

type FilterType = 'all' | AnnouncementType;

// Helper para detectar mime type desde URL
const getMimeTypeFromUrl = (url: string): string => {
  // Si es una URL del CDN sin extensión, asumir que es imagen
  if (url.includes('/cdn/files/') || url.includes('/api/v1/cdn/')) {
    return 'image/jpeg';
  }

  const ext = url.split('.').pop()?.toLowerCase();
  const mimeTypes: Record<string, string> = {
    'pdf': 'application/pdf',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'mp4': 'video/mp4',
    'webm': 'video/webm',
  };
  return mimeTypes[ext || ''] || 'application/octet-stream';
};

export const Announcements = () => {
  const { t } = useI18n();
  const { formatDate } = useDateFormatter();
  const toast = useToast();
  const { openFile, openFileByUrl } = useFileViewer();
  const { announcementRepo, userRepo } = useServices();
  const { selectedSectionId, hasSections } = useSection();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const [votingPoll, setVotingPoll] = useState<string | null>(null);

  const fetchAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const response = await announcementRepo.getAnnouncements({
        page: 1,
        limit: 100,
        sectionId: selectedSectionId ?? undefined,
      });
      setAnnouncements(response.data);
    } catch (error) {
      toast.error('Error al cargar comunicados');
    } finally {
      setLoading(false);
    }
  }, [announcementRepo, selectedSectionId, toast]);

  useEffect(() => {
    const init = async () => {
      const user = await userRepo.getCurrentUser();
      setCurrentUser(user);
      fetchAnnouncements();
    };
    init();
  }, [userRepo, fetchAnnouncements]);

  const handleVote = async (annId: string, optId: string) => {
    if (!currentUser) return;
    setVotingPoll(annId);
    try {
      await announcementRepo.voteInPoll(annId, optId, currentUser.id);
      toast.success('Voto registrado');
      await fetchAnnouncements();
    } catch (e) {
      toast.warning('Ya has votado o hubo un error.');
    } finally {
      setVotingPoll(null);
    }
  };

  const filteredAnnouncements = filter === 'all'
    ? announcements
    : announcements.filter(a => a.type === filter);

  const getPriorityIcon = (type: AnnouncementType) => {
    switch (type) {
      case AnnouncementType.URGENT:
        return <AlertTriangle size={18} />;
      case AnnouncementType.IMPORTANT:
        return <Bell size={18} />;
      case AnnouncementType.EVENT:
        return <PartyPopper size={18} />;
      default:
        return <Info size={18} />;
    }
  };

  const getPriorityStyles = (type: AnnouncementType) => {
    switch (type) {
      case AnnouncementType.URGENT:
        return {
          border: 'border-l-red-500',
          bg: 'bg-red-50',
          text: 'text-red-700',
          badge: 'bg-red-100 text-red-700 border-red-200',
        };
      case AnnouncementType.IMPORTANT:
        return {
          border: 'border-l-amber-500',
          bg: 'bg-amber-50',
          text: 'text-amber-700',
          badge: 'bg-amber-100 text-amber-700 border-amber-200',
        };
      case AnnouncementType.EVENT:
        return {
          border: 'border-l-emerald-500',
          bg: 'bg-emerald-50',
          text: 'text-emerald-700',
          badge: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        };
      default:
        return {
          border: 'border-l-blue-500',
          bg: 'bg-blue-50',
          text: 'text-blue-700',
          badge: 'bg-blue-100 text-blue-700 border-blue-200',
        };
    }
  };

  const filterButtons: { value: FilterType; label: string; icon?: React.ReactNode }[] = [
    { value: 'all', label: 'Todos' },
    { value: AnnouncementType.URGENT, label: t('admin.ann.priority.urgent'), icon: <AlertTriangle size={14} /> },
    { value: AnnouncementType.IMPORTANT, label: t('admin.ann.priority.important'), icon: <Bell size={14} /> },
    { value: AnnouncementType.EVENT, label: t('admin.ann.priority.event'), icon: <PartyPopper size={14} /> },
    { value: AnnouncementType.INFO, label: t('admin.ann.priority.info'), icon: <Info size={14} /> },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Megaphone className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">{t('menu.announcements')}</h2>
            <p className="text-sm text-slate-500">{filteredAnnouncements.length} comunicados</p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        <Filter size={16} className="text-slate-400 shrink-0" />
        {filterButtons.map((btn) => (
          <button
            key={btn.value}
            onClick={() => setFilter(btn.value)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              filter === btn.value
                ? 'bg-primary-100 text-primary-700 border border-primary-300'
                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            {btn.icon}
            {btn.label}
          </button>
        ))}
        {hasSections && (
          <div className="ml-auto shrink-0 min-w-[200px]">
            <SectionSelector />
          </div>
        )}
      </div>

      {/* Lista de comunicados */}
      {filteredAnnouncements.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-100">
          <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">No hay comunicados disponibles</p>
          <p className="text-sm text-slate-400 mt-1">
            {filter !== 'all' ? 'Prueba con otro filtro' : 'Los comunicados aparecerán aquí'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredAnnouncements.map((ann) => {
            const styles = getPriorityStyles(ann.type);
            const hasVoted = ann.poll?.hasVoted;
            const totalVotes = ann.poll ? ann.poll.options.reduce((acc, curr) => acc + curr.votes, 0) : 0;

            return (
              <div
                key={ann.id}
                className={`bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden`}
              >
                {/* Barra de color superior */}
                <div className={`h-1 ${styles.border.replace('border-l-', 'bg-')}`} />

                <div className="p-5">
                  {/* Header del anuncio */}
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${styles.badge}`}>
                          {getPriorityIcon(ann.type)}
                          {t(`admin.ann.priority.${ann.type.toLowerCase()}`)}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-slate-800">{ann.title}</h3>
                    </div>
                    <div className="text-right text-sm text-slate-500 shrink-0">
                      <div className="flex items-center gap-1">
                        <Calendar size={14} />
                        {formatDate(ann.date)}
                      </div>
                    </div>
                  </div>

                  {/* Contenido */}
                  <p className="text-slate-600 leading-relaxed whitespace-pre-wrap mb-4">
                    {ann.content}
                  </p>

                  {/* Adjunto */}
                  {ann.attachmentUrl && (
                    <button
                      onClick={() => {
                        const url = ann.attachmentUrl!;
                        const mime = getMimeTypeFromUrl(url);
                        const isFullUrl = url.startsWith('http://') || url.startsWith('https://');

                        if (isFullUrl) {
                          // URL completa del CDN - usar visor directo
                          openFileByUrl(url, t('admin.ann.attach_view'), mime);
                        } else if (ann.attachmentFileId) {
                          // Tiene ID de archivo - obtener presigned URL
                          openFile(ann.attachmentFileId, t('admin.ann.attach_view'), mime);
                        } else {
                          // Fallback: tratar como ID
                          openFile(url, t('admin.ann.attach_view'), mime);
                        }
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm font-medium mb-4"
                    >
                      <Paperclip size={16} />
                      {t('admin.ann.attach_view')}
                    </button>
                  )}

                  {/* Poll */}
                  {ann.poll && (
                    <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                      <h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                        <BarChart2 size={18} className="text-primary-600" />
                        {ann.poll.question || ann.poll.title}
                      </h4>

                      {hasVoted ? (
                        // Mostrar resultados
                        <div className="space-y-2">
                          {ann.poll.options.map((opt) => {
                            const percentage = totalVotes > 0 ? Math.round((opt.votes / totalVotes) * 100) : 0;
                            const isSelected = ann.poll?.userVotedOptionIds?.includes(opt.id);

                            return (
                              <div key={opt.id} className="relative">
                                <div
                                  className={`h-10 rounded-lg overflow-hidden ${isSelected ? 'bg-primary-100' : 'bg-slate-100'}`}
                                >
                                  <div
                                    className={`h-full transition-all duration-500 ${isSelected ? 'bg-primary-200' : 'bg-slate-200'}`}
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                                <div className="absolute inset-0 flex items-center justify-between px-3">
                                  <span className={`text-sm font-medium ${isSelected ? 'text-primary-700' : 'text-slate-700'}`}>
                                    {isSelected && <CheckCircle size={14} className="inline mr-1.5" />}
                                    {opt.text}
                                  </span>
                                  <span className="text-sm font-bold text-slate-600">
                                    {percentage}%
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                          <p className="text-xs text-slate-500 mt-2 text-center">
                            {totalVotes} {totalVotes === 1 ? 'voto' : 'votos'} totales
                          </p>
                        </div>
                      ) : (
                        // Mostrar opciones para votar
                        <div className="space-y-2">
                          {ann.poll.options.map((opt) => (
                            <button
                              key={opt.id}
                              onClick={() => handleVote(ann.id, opt.id)}
                              disabled={votingPoll === ann.id}
                              className="w-full text-left px-4 py-3 bg-white border border-slate-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-all text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {votingPoll === ann.id ? (
                                <Loader2 size={16} className="animate-spin inline mr-2" />
                              ) : null}
                              {opt.text}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
